import * as React from "react";
import { Callout, DirectionalHint, useTheme } from "@fluentui/react";
import { getLocalizedString } from "./LocalizedText";
import { UiSchema } from "@rjsf/utils";
import { RjsfUtils } from "./RjsfUtils";
import { useMediaQuery, useStringsSanitizer } from "../../hooks";
import { TrustedHtmlComponent } from "../TrustedHtmlComponent";
import { useAppSelector } from "../../hooks";

/**
 * The props interface for the HelpField
 */
interface IHelpFieldProps {
  /**
   * The help Text
   */
  helpText?: string;

  /**
   * The ID for the associated input field.
   */
  inputId: string;

  /**
   * The ID that should be used for this help element.
   */
  id: string;

  /**
   * The current UI schema
   */
  uiSchema: UiSchema;
}

/**
 * The props for the test input form help fields
 */
interface ITestInputFormHelpFieldProps extends IHelpFieldProps {
  /**
   * The localized help string
   */
  localizedHelp: string;
}

/**
 * A help field where the help is displayed as a callout when the field has focus
 *
 * @param props - the component properties
 * @returns the component
 */
const CalloutHelpField: React.FC<ITestInputFormHelpFieldProps> = (props) => {
  const { inputId, localizedHelp, id } = props;
  const focusedFieldId = useAppSelector((s) => s.testInput.focusedFieldId);
  const theme = useTheme();

  // This width is tuned to make sure that even in desktop mode
  // we still have room to show the callout beside the element.
  const calloutBelow = useMediaQuery("only screen and (max-width: 900px)");

  const policy = useStringsSanitizer();
  const element = document.querySelector<HTMLElement>(`#${inputId}`);
  const elementType = element instanceof HTMLInputElement ? element.type : element?.tagName;

  // For password fields, the element we want to use to focus the callout is the parent element
  // since it includes the "reveal password" button and is wider
  const targetElement = elementType === "password" ? element?.parentElement : element;
  const isHidden = !!element && focusedFieldId !== element.id;
  const calloutBackgroundColor = theme.semanticColors.inputBackground;
  const calloutBorderColor = theme.semanticColors.inputBorder;

  const directionalHint = calloutBelow ? DirectionalHint.bottomAutoEdge : DirectionalHint.rightCenter;
  const directionalHintForRTL = calloutBelow ? DirectionalHint.bottomAutoEdge : DirectionalHint.leftCenter;

  return (
    <>
      {element && (
        <Callout
          id={id}
          styles={{
            root: {
              maxWidth: 250,
              overflowWrap: "break-word",
              border: `1px solid ${calloutBorderColor}`,
            },
            calloutMain: {
              padding: 15,
              backgroundColor: calloutBackgroundColor,
              color: theme.semanticColors.bodyText,
            },
            beak: {
              backgroundColor: calloutBackgroundColor,
            },
            beakCurtain: {
              backgroundColor: calloutBackgroundColor,
            },
          }}
          gapSpace={15}
          target={targetElement}
          directionalHint={directionalHint}
          directionalHintForRTL={directionalHintForRTL}
          directionalHintFixed={true}
          coverTarget={false}
          hidden={isHidden}
        >
          <TrustedHtmlComponent tagName="div" trustedHtml={policy.createHTML(localizedHelp)} />
        </Callout>
      )}
    </>
  );
};

/**
 * The help field that is always displayed inline with the field
 *
 * @param props - the component properties
 * @returns the component
 */
const InlineHelpField: React.FC<ITestInputFormHelpFieldProps> = ({ id, localizedHelp }) => {
  const policy = useStringsSanitizer();
  return <TrustedHtmlComponent tagName="p" id={id} className="field-help" trustedHtml={policy.createHTML(localizedHelp)} />;
};

/**
 * The help field
 *
 * @param props - the component props.
 * @returns - the markup
 */
export const HelpField: React.FC<IHelpFieldProps> = (props) => {
  const { helpText, uiSchema } = props;
  const { helpType } = RjsfUtils.getStringOptions(uiSchema);

  const localizedHelp = getLocalizedString(helpText);
  const isCallout = helpType === "callout";

  return (
    <>
      {localizedHelp &&
        (isCallout ? (
          <CalloutHelpField {...props} localizedHelp={localizedHelp} />
        ) : (
          <InlineHelpField {...props} localizedHelp={localizedHelp} />
        ))}
    </>
  );
};
